const AUTO_SEGMENTS = [
  {
    id: "seg-routine-care",
    name: "Routine care",
    category: "Behavior",
    description: "Returns for planned care and has a reliable attendance history.",
    source: "ilia",
  },
  {
    id: "seg-reactive-care",
    name: "Follow-up overdue",
    category: "Behavior",
    description: "Has a missed visit or repeated service interest without a completed booking.",
    source: "ilia",
  },
  {
    id: "seg-schedule-sensitive",
    name: "Schedule-sensitive",
    category: "Behavior",
    description: "Has expressed a timing preference, moved an appointment, or repeatedly contacts at a narrow time.",
    source: "ilia",
  },
  {
    id: "seg-price-questions",
    name: "Price or payment questions",
    category: "Behavior",
    description: "Asked about price, payment, insurance, instalments, or what a service includes.",
    source: "ilia",
  },
  {
    id: "seg-heart-care",
    name: "Heart care",
    category: "Service",
    description: "Discussed or booked cardiology, ECG, Holter monitoring, or a cardiac package.",
    source: "ilia",
    offer: {
      title: "ECG follow-up after cardiology",
      nextServiceId: "s-ecg",
      afterServiceIds: ["s-cardio", "s-holter"],
      exclusionWindowDays: 90,
    },
  },
  {
    id: "seg-thyroid-care",
    name: "Thyroid care",
    category: "Service",
    description: "Discussed or booked thyroid ultrasound services.",
    source: "ilia",
    offer: {
      title: "Blood test panel after thyroid ultrasound",
      nextServiceId: "s-blood",
      afterServiceIds: ["s-thyroid"],
      exclusionWindowDays: 90,
    },
  },
  {
    id: "seg-skin-care",
    name: "Skin care",
    category: "Service",
    description: "Discussed or booked a dermatology consultation.",
    source: "ilia",
  },
  {
    id: "seg-under-35",
    name: "Under 35",
    category: "Age",
    description: "Age band imported from the connected customer record.",
    source: "imported",
    membershipLocked: true,
  },
  {
    id: "seg-age-35-54",
    name: "Age 35-54",
    category: "Age",
    description: "Age band imported from the connected customer record.",
    source: "imported",
    membershipLocked: true,
  },
  {
    id: "seg-age-55-plus",
    name: "Age 55+",
    category: "Age",
    description: "Age band imported from the connected customer record.",
    source: "imported",
    membershipLocked: true,
  },
  {
    id: "seg-high-potential",
    name: "High potential",
    category: "Commercial",
    description: "Current opportunity value is at least ₾150.",
    source: "derived",
    membershipLocked: true,
  },
  {
    id: "seg-marketing-contactable",
    name: "Marketing contactable",
    category: "Commercial",
    description: "The CRM record currently contains marketing consent.",
    source: "imported",
    membershipLocked: true,
  },
];

function customerEvidence(customer, conversations, bookings) {
  const customerConversations = conversations.filter((item) => item.customerId === customer.id);
  const customerBookings = bookings.filter((item) => item.customerId === customer.id);
  const text = [
    ...customer.qualification.filter((item) => ["customer", "observed", "imported"].includes(item.source)).map((item) => item.value),
    ...customer.notes.map((item) => item.text),
    ...customerConversations.flatMap((item) => (item.captured || []).filter((field) => ["customer", "observed"].includes(field.source)).map((field) => field.value)),
    ...customerConversations.flatMap((item) => item.messages.filter((message) => message.from === "customer").map((message) => message.text)),
  ].join(" ").toLowerCase();
  const serviceIds = new Set([
    ...customerBookings.map((item) => item.serviceId),
    ...customerConversations.map((item) => item.serviceId).filter(Boolean),
  ]);
  return { customerConversations, customerBookings, text, serviceIds };
}

function classify(segment, customer, evidence) {
  const { customerConversations, customerBookings, text, serviceIds } = evidence;
  const hasNoShow = customerBookings.some((item) => item.state === "noshow") || customer.history.noShows > 0;
  const hasTimeSignal = customer.history.moved > 0
    || customerConversations.some((item) => item.captured?.some((field) => ["customer", "observed"].includes(field.source) && /preferred time/i.test(field.key)))
    || /only writes|morning|evening|move my|reschedule/.test(text);

  switch (segment.id) {
    case "seg-routine-care":
      return customer.history.visits >= 2 && !hasNoShow
        ? `${customer.history.visits} visits and no recorded no-shows`
        : null;
    case "seg-reactive-care":
      return hasNoShow || (/asked/.test(text) && /booked none/.test(text))
        ? hasNoShow ? "Missed care is present in booking history" : "Repeated interest without a completed booking"
        : null;
    case "seg-schedule-sensitive":
      return hasTimeSignal ? "Conversation or booking history contains a timing constraint" : null;
    case "seg-price-questions":
      return /price|cost|payment|pay in|instalment|insurance|cover|include/.test(text)
        ? "A price, payment, insurance, or package question appears in the conversation"
        : null;
    case "seg-heart-care":
      return ["s-cardio", "s-ecg", "s-holter"].some((id) => serviceIds.has(id))
        || /cardio|heart|ecg|holter/.test(text)
        ? "Heart-related service intent appears in conversation or booking history"
        : null;
    case "seg-thyroid-care":
      return serviceIds.has("s-thyroid") || /thyroid/.test(text)
        ? "Thyroid service intent appears in conversation or booking history"
        : null;
    case "seg-skin-care":
      return serviceIds.has("s-derma") || /dermat/.test(text)
        ? "Dermatology intent appears in conversation or booking history"
        : null;
    case "seg-under-35":
      return ["18-24", "25-34"].includes(customer.ageBand) ? `Age band ${customer.ageBand} in CRM` : null;
    case "seg-age-35-54":
      return ["35-44", "45-54"].includes(customer.ageBand) ? `Age band ${customer.ageBand} in CRM` : null;
    case "seg-age-55-plus":
      return ["55-64", "65+"].includes(customer.ageBand) ? `Age band ${customer.ageBand} in CRM` : null;
    case "seg-high-potential":
      return Number(customer.value) >= 150 ? `Potential sale is ₾${Number(customer.value).toLocaleString("en-US")}` : null;
    case "seg-marketing-contactable":
      return customer.consent?.marketing ? "Marketing consent is present in CRM" : null;
    default:
      return null;
  }
}

export function refreshSegments({ customers = [], conversations = [], bookings = [] }, previous = [], refreshedAt = new Date().toISOString()) {
  const previousById = new Map(previous.map((segment) => [segment.id, segment]));
  const automatic = AUTO_SEGMENTS.map((definition) => {
    const prior = previousById.get(definition.id) || {};
    const manualIncludeIds = definition.membershipLocked ? [] : (prior.manualIncludeIds || []);
    const manualExcludeIds = definition.membershipLocked ? [] : (prior.manualExcludeIds || []);
    const memberEvidence = {};
    const computedIds = customers.flatMap((customer) => {
      const reason = classify(definition, customer, customerEvidence(customer, conversations, bookings));
      if (!reason) return [];
      memberEvidence[customer.id] = reason;
      return [customer.id];
    });
    const memberIds = Array.from(new Set([
      ...computedIds.filter((id) => !manualExcludeIds.includes(id)),
      ...manualIncludeIds,
    ]));
    manualIncludeIds.forEach((id) => {
      memberEvidence[id] = "Added by a team member";
    });
    return {
      ...definition,
      memberIds,
      memberEvidence,
      manualIncludeIds,
      manualExcludeIds,
      lastRefreshed: refreshedAt,
    };
  });
  const automaticIds = new Set(AUTO_SEGMENTS.map((segment) => segment.id));
  const manual = previous.filter((segment) => segment.source === "staff" && !automaticIds.has(segment.id));
  return [...automatic, ...manual];
}

export function createInitialSegments(state, refreshedAt) {
  return refreshSegments(state, [], refreshedAt);
}

export function segmentMembers(segment, customers = []) {
  const ids = new Set(segment?.memberIds || []);
  return customers.filter((customer) => ids.has(customer.id));
}

export function offerCandidates(segment, { customers = [], bookings = [] }) {
  if (!segment?.offer) return [];
  const { nextServiceId, afterServiceIds, exclusionWindowDays = 90 } = segment.offer;
  const exclusionCutoff = Date.now() - exclusionWindowDays * 24 * 60 * 60 * 1000;
  return segmentMembers(segment, customers).filter((customer) => {
    if (customer.mergePending) return false;
    const relevant = bookings.filter((booking) => booking.customerId === customer.id && booking.state !== "cancelled");
    const hasStartingService = relevant.some((booking) => afterServiceIds.includes(booking.serviceId) && ["confirmed", "completed"].includes(booking.state));
    const alreadyHasNext = relevant.some((booking) => booking.serviceId === nextServiceId
      && (booking.state === "confirmed" || (booking.state === "completed" && new Date(booking.start).getTime() >= exclusionCutoff)));
    return hasStartingService && !alreadyHasNext;
  });
}

const OPPORTUNITY_DEFINITIONS = [
  { id: "heart-follow-up", segmentId: "seg-heart-care", kind: "cross_sell" },
  { id: "thyroid-follow-up", segmentId: "seg-thyroid-care", kind: "cross_sell" },
  { id: "stalled-care", segmentId: "seg-reactive-care", kind: "recovery" },
  { id: "price-decisions", segmentId: "seg-price-questions", kind: "open_deal" },
];

const ACTIVE_STAGES = new Set(["New", "Qualifying", "Qualified", "Offered"]);

function observedRates({ conversations = [], bookings = [] }) {
  const conversationCustomerIds = new Set(conversations.map((item) => item.customerId).filter(Boolean));
  const bookedCustomerIds = new Set(
    bookings
      .filter((item) => item.state !== "cancelled")
      .map((item) => item.customerId)
      .filter(Boolean),
  );
  const bookedFromConversation = Array.from(conversationCustomerIds)
    .filter((customerId) => bookedCustomerIds.has(customerId)).length;
  const bookingRate = conversationCustomerIds.size
    ? bookedFromConversation / conversationCustomerIds.size
    : 0;
  const attended = bookings.filter((item) => item.state === "completed").length;
  const noShows = bookings.filter((item) => item.state === "noshow").length;
  const showRate = attended + noShows ? attended / (attended + noShows) : 0;

  return {
    bookingRate,
    showRate,
    conversationSample: conversationCustomerIds.size,
    attendanceSample: attended + noShows,
  };
}

function currentOpportunityValue(customer) {
  return Number(customer?.commercial?.openValue ?? customer?.value ?? 0);
}

/**
 * Turns reusable audience segments into transparent commercial actions.
 * Gross value is never presented as revenue: expectedValue applies only
 * observed booking and attendance rates from the current workspace snapshot.
 */
export function buildOpportunityModels(state = {}) {
  const {
    segments = [], customers = [], bookings = [], conversations = [], services = [],
  } = state;
  const rates = observedRates({ conversations, bookings });

  return OPPORTUNITY_DEFINITIONS.flatMap((definition) => {
    const segment = segments.find((item) => item.id === definition.segmentId);
    if (!segment) return [];
    const members = segmentMembers(segment, customers);
    const nextService = services.find((service) => service.id === segment.offer?.nextServiceId) || null;
    let eligible;
    let contactable;

    if (definition.kind === "cross_sell") {
      eligible = offerCandidates(segment, { customers, bookings });
      contactable = eligible.filter((customer) => customer.consent?.marketing);
    } else if (definition.kind === "open_deal") {
      eligible = members.filter((customer) => ACTIVE_STAGES.has(customer.stage) && !customer.mergePending);
      contactable = eligible.filter((customer) => customer.consent?.messaging !== false);
    } else {
      eligible = members.filter((customer) => !customer.mergePending);
      contactable = eligible.filter((customer) => customer.consent?.marketing);
    }

    const valueFor = (customer) => definition.kind === "cross_sell"
      ? Number(nextService?.price || 0)
      : currentOpportunityValue(customer);
    const addressableValue = contactable.reduce((sum, customer) => sum + valueFor(customer), 0);
    const expectedValue = addressableValue * rates.bookingRate * rates.showRate;

    return [{
      ...definition,
      segment,
      members,
      eligible,
      contactable,
      excluded: eligible.filter((customer) => !contactable.some((item) => item.id === customer.id)),
      nextService,
      valueFor,
      addressableValue,
      expectedValue,
      averageValue: contactable.length ? addressableValue / contactable.length : 0,
      ...rates,
    }];
  }).sort((a, b) => b.expectedValue - a.expectedValue || b.addressableValue - a.addressableValue);
}

/**
 * Deduplicates people across overlapping segments. For each person, the model
 * keeps the highest-value available action instead of adding every segment.
 */
export function summarizeOpportunityModels(models = []) {
  const uniquePeople = new Map();
  models.forEach((model) => {
    model.contactable.forEach((customer) => {
      const addressableValue = model.valueFor(customer);
      const expectedValue = addressableValue * model.bookingRate * model.showRate;
      const current = uniquePeople.get(customer.id);
      if (!current || expectedValue > current.expectedValue) {
        uniquePeople.set(customer.id, { customer, model, addressableValue, expectedValue });
      }
    });
  });

  const rows = Array.from(uniquePeople.values());
  return {
    people: rows.length,
    addressableValue: rows.reduce((sum, row) => sum + row.addressableValue, 0),
    expectedValue: rows.reduce((sum, row) => sum + row.expectedValue, 0),
    overlapsRemoved: models.reduce((sum, model) => sum + model.contactable.length, 0) - rows.length,
  };
}

export const SEGMENT_CATEGORIES = ["Behavior", "Service", "Age", "Commercial"];
